import {UserData} from 'amazon-cognito-identity-js'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Helmet} from 'react-helmet-async'
import {Routes, useNavigate} from 'react-router-dom'
import {useSetCrumbs} from '../../components/crumbtrail'
import {ErrorComponent} from '../../components/errors'
import {PageHeader, PageHeaderContent} from '../../components/page-styles'
import {Button} from '../../components/library/buttons'
import {FullScreenOverlay} from '../../components/library/modal/modal'
import styled, {keyframes} from 'styled-components'
import {Loader} from '../../components/loader'
import {useAuth} from '../../lib/auth'
import {AuthStateAuth, Authenticator} from '../../lib/cognito'
import {Route} from '../../lib/router'
import {useToast} from '../../lib/toast'
import {OrganisationNode} from '../../organisation'
import {MfaSetup, setMfaPref} from './mfa'
import {Finish} from './mfa/finish'
import {ProfileFields} from './profile-fields'
import {VerifyPassword} from './verify-password'

const ProfileContent = styled.div`
  min-width: 350px;
  max-width: 450px;
  flex-grow: 1;
`

export const StyledSection = styled.div`
  margin-bottom: 50px;
`

export const SectionContent = styled.div`
  padding: 10px 0;
  font-size: ${({theme}) => theme.fontSizeL};
  line-height: ${({theme}) => theme.lineHeightL};
`

export const Label = styled.div`
  color: ${({theme}) => theme.grey100};
  font-size: ${({theme}) => theme.fontSizeS};
  line-height: ${({theme}) => theme.lineHeightCompact};
`

export const SectionLabel = styled(Label)`
  margin-bottom: 16px;
`

const pulse = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
`

const SectionLoading = styled.div`
  padding: 12.5px 0;
`

const SectionContentLoading = styled.div`
  height: 10px;
  border-radius: ${({theme}) => theme.borderRadiusPill};
  background: ${({theme}) => theme.grey10};
  width: 100px;
  position: relative;
  overflow: hidden;
  animation: ${pulse} 1s forwards infinite;
`

const ButtonLoading = styled.div`
  padding: 12.5px 0;
`

const ButtonContentLoading = styled.div`
  height: 5px;
  border-radius: ${({theme}) => theme.borderRadiusPill};
  background: ${({theme}) => theme.blue};
  width: 50px;
  position: relative;
  overflow: hidden;
  animation: ${pulse} 1s forwards infinite;
`

const ReadonlyWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  padding: 0 20px;
  border: 1px solid ${props => props.theme.grey10};
  background: white;
  border-radius: ${props => props.theme.borderRadius};
`

export const Profile = ({organisation}: {organisation: OrganisationNode}) => {
  const {auth, authState} = useAuth()
  const [userData, setUserData] = useState<UserData>()
  const [error] = useState<Error>()
  const makeToast = useToast()
  const navigate = useNavigate()

  const getUserData = useCallback((): Promise<UserData> => {
    return new Promise((resolve, reject) => {
      const user = auth.pool.getCurrentUser()
      if (user) {
        user.getSession(() => {
          user.getUserData(
            (err, data) => {
              if (err) {
                makeToast(err.message)
                reject(err)
              }
              if (data) {
                setUserData(data)
                resolve(data)
              }
            },
            {bypassCache: true}
          )
        })
      }
    })
  }, [auth.pool, makeToast])

  if (!authState.authenticated) {
    navigate('/login')
    return <Loader />
  }

  if (error) {
    return <ErrorComponent error={error} />
  }

  return (
    <Routes>
      <Route
        path="/"
        render={() => (
          <Dash
            userData={userData}
            authState={authState}
            refreshUserData={getUserData}
            auth={auth}
            organisationId={organisation.id}
            organisation={organisation}
          />
        )}
      />
      <Route
        path="/mfa-setup/*"
        render={() => (
          <MfaSetup
            authState={authState}
            auth={auth}
            onComplete={() => {
              navigate(`/${organisation.id}/profile/mfa-enabled`)
            }}
          />
        )}
      />
      <Route
        path="/mfa-enabled"
        render={() => (
          <FullScreenOverlay>
            <Finish
              icon="padlock"
              title="Two-step authentication enabled"
              message="You’ve successfully enabled two-step authentication."
              onClick={() => navigate(`/${organisation.id}/profile`)}
              buttonText="Return to Profile"
            />
          </FullScreenOverlay>
        )}
      />
      <Route
        path="/mfa-disabled"
        render={() => (
          <FullScreenOverlay>
            <Finish
              icon="unlock"
              title="Two-step authentication disabled"
              message="You’ve successfully disabled two-step authentication."
              onClick={() => navigate(`/${organisation.id}/profile`)}
              buttonText="Return to Profile"
            />
          </FullScreenOverlay>
        )}
      />
    </Routes>
  )
}

const Layout = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  padding: 15px 20px;
`

const Dash = ({
  auth,
  userData,
  authState,
  refreshUserData,
  organisationId,
  organisation
}: {
  authState: AuthStateAuth
  auth: Authenticator
  userData?: UserData
  refreshUserData(): Promise<UserData>
  organisationId?: string
  organisation: OrganisationNode
}) => {
  // refresh user data on load
  useEffect(() => {
    refreshUserData()
  }, [refreshUserData])

  // Don't show MFA options if user logged in via SSO
  // WIP: const showMFA = !authState.ssoLogin
  const showMFA = !authState.ssoLogin

  useSetCrumbs(['Profile'])

  return (
    <>
      <Helmet>
        <title>Profile</title>
      </Helmet>
      <PageHeader>
        <PageHeaderContent>
          <h2>Profile</h2>
        </PageHeaderContent>
      </PageHeader>
      <Layout>
        <ProfileContent>
          {organisationId && (
            <StyledSection>
              <SectionLabel>
                <b>Personal details</b>
              </SectionLabel>
              <SectionLabel>
                Edit your profile details for the organization
                <b>{` ${organisation.name} ${organisationId}`}</b>
              </SectionLabel>
              <ProfileFields organisationId={organisationId} />
            </StyledSection>
          )}
        </ProfileContent>
        <ProfileContent>
          {/*  WIP - once designs are complete (see existing profile page) */}
          {showMFA && organisationId && (
            <StyledSection>
              <SectionLabel>
                <b>Two-factor authentication (2FA)</b>
              </SectionLabel>
              <SectionLabel>
                This will require you to enter a 6 digit code when logging in.
              </SectionLabel>
              <SectionContent>
                {userData && (
                  <MfaSection
                    organisationId={organisationId}
                    userData={userData}
                    auth={auth}
                    authState={authState}
                  />
                )}
              </SectionContent>
            </StyledSection>
          )}
        </ProfileContent>
      </Layout>
    </>
  )
}

export const SectionLoader = () => {
  return (
    <>
      <SectionLoading>
        <SectionContentLoading />
      </SectionLoading>
      <ButtonLoading>
        <ButtonContentLoading />
      </ButtonLoading>
    </>
  )
}

const MfaSection = ({
  organisationId,
  userData,
  auth,
  authState
}: {
  organisationId: string
  userData: UserData | undefined
  auth: Authenticator
  authState: AuthStateAuth
}) => {
  const makeToast = useToast()
  const [loading, setLoading] = useState(true)
  const [verifyThen, setVerifyThen] = useState<() => void>()

  const mfaEnabled = useMemo(() => {
    setLoading(false)
    return (
      userData?.UserMFASettingList &&
      userData?.UserMFASettingList.includes('SOFTWARE_TOKEN_MFA')
    )
  }, [userData, setLoading])
  const navigate = useNavigate()

  const disableMfa = async () => {
    setLoading(true)
    let res: string | undefined = undefined

    try {
      res = await setMfaPref(auth.pool, false)
    } catch (err) {
      if (!(err instanceof Error))
        throw new Error(`Unknown error ${String(err)}`)
      // Edge case where MFA setup isn't completed, so is unable to be updated to disabled
      // This shouldn't happen but has been seen occasionally.
      // this enables the MFA setup to complete, so it can then be disabled
      if (
        err.message.match(/not verified/i) ||
        err.message.match(/not set up/i)
      ) {
        navigate(`/${organisationId}/profile/mfa-setup`)
        return
      } else {
        makeToast(err.message, 'error')
      }
    } finally {
      setLoading(false)
      setVerifyThen(undefined)
    }

    if (res) {
      navigate(`/${organisationId}/profile/mfa-disabled`)
    }
  }

  if (loading) {
    return <Loader />
  }

  return (
    <>
      {verifyThen && (
        <VerifyPassword
          title="Disable two-step authentication"
          auth={auth}
          authState={authState}
          onVerified={verifyThen}
          onClose={() => setVerifyThen(undefined)}
        >
          To disable two-step authentication, please enter your password.
        </VerifyPassword>
      )}
      {mfaEnabled ? (
        <ReadonlyWrapper>
          <Label>Disable 2FA for all organisations</Label>
          <Button
            appearance="secondary-light"
            onClick={() => {
              // The extra call back nesting is required, as react sees a callback and calls it with current state immediately.
              setVerifyThen(() => () => disableMfa())
            }}
          >
            Disable
          </Button>
        </ReadonlyWrapper>
      ) : (
        <ReadonlyWrapper>
          <Label>Enable 2FA for all organisations</Label>
          <Button
            appearance="secondary"
            onClick={() => {
              // The mfa-setup route will verify password, so we don't need to do it here
              navigate(`/${organisationId}/profile/mfa-setup`)
            }}
          >
            Enable
          </Button>
        </ReadonlyWrapper>
      )}
    </>
  )
}
